// ════════════════════════════════════════════════════════════════
// Prospection — référentiel des statuts + helpers d'affichage
// ════════════════════════════════════════════════════════════════
import { extractId, toIri } from 'src/utils/apiPlatform'

// Les statuts sont figés côté back (constantes de EntrepriseProspect).
// Ce fichier est leur miroir côté front : un seul endroit pour les
// libellés, l'ordre et le "ton" visuel. Il sera réutilisé tel quel
// par la future vue "Mes relances" / tableau de bord.

export const STATUTS_PROSPECT = [
  { value: 'a_qualifier', label: 'À qualifier', tone: 'neutral' },
  { value: 'devis_a_envoyer', label: 'Devis à envoyer', tone: 'info' },
  { value: 'devis_envoye', label: 'Devis envoyé', tone: 'warning' },
  { value: 'signe', label: 'Signé', tone: 'positive' },
  { value: 'perdu', label: 'Perdu', tone: 'negative' }
]

export const STATUT_PAR_DEFAUT = 'a_qualifier'
export const STATUTS_CLOS = ['signe', 'perdu']
export const STATUTS_EN_COURS = STATUTS_PROSPECT
  .map(s => s.value)
  .filter(v => !STATUTS_CLOS.includes(v))

export const getStatut = (value) =>
  STATUTS_PROSPECT.find(s => s.value === value) ?? STATUTS_PROSPECT[0]

export const isClos = (prospect) => STATUTS_CLOS.includes(prospect?.statut)

// ── Recherche ──────────────────────────────────────────────────────
// Insensible à la casse, aux accents et aux espaces multiples :
// "Formation  Bureautique" == "formation bureautique".
export const normaliser = (texte) => String(texte ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim()

// ── Dates ──────────────────────────────────────────────────────────
// L'API échange des dates "YYYY-MM-DD", l'interface affiche "DD/MM/YYYY".

export const todayIso = () => {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

export const isoToFr = (iso) => {
  if (!iso) return ''
  const [y, m, d] = String(iso).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

export const frToIso = (fr) => {
  if (!fr) return null
  const [d, m, y] = fr.split('/')
  return y && m && d ? `${y}-${m}-${d}` : null
}

export const isValidFrDate = (fr) => {
  if (!fr) return true
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(fr)) return false
  const [d, m, y] = fr.split('/').map(Number)
  // Calcul en UTC de bout en bout : un mélange heure locale / UTC
  // (toISOString) décale la date d'un jour en France (UTC+1/+2).
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

/** Date "JJ/MM/AAAA" postérieure à aujourd'hui ? (vide = non) */
export const isFutureFrDate = (fr) => {
  const iso = frToIso(fr)
  return !!iso && iso > todayIso()
}

/** Nombre de jours entre aujourd'hui et la date (négatif = dépassée). */
export const joursAvant = (iso) => {
  if (!iso) return null
  const cible = new Date(`${String(iso).slice(0, 10)}T00:00:00`)
  const auj = new Date(`${todayIso()}T00:00:00`)
  return Math.round((cible - auj) / 86400000)
}

/** "YYYY-MM-DD" + n jours (pour proposer une date de relance) */
export const ajouterJoursIso = (iso, n) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d + n))
  return date.toISOString().slice(0, 10)
}

export const isEnRetard = (prospect) => {
  if (isClos(prospect) || !prospect?.dateEcheance) return false
  return joursAvant(prospect.dateEcheance) < 0
}

// ── Montants ───────────────────────────────────────────────────────
// Le montant arrive en string (decimal Doctrine).

const euro = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2
})

export const formatMontant = (montant) => {
  if (montant === null || montant === undefined || montant === '') return null
  const n = Number(montant)
  return Number.isNaN(n) ? null : euro.format(n)
}

// ── Normalisation des réponses API ─────────────────────────────────
// Les composants ne manipulent jamais le format brut (hydra, @id, IRI).

export const normalizeType = (t) => {
  if (!t) return null
  if (typeof t !== 'object') {
    return { id: extractId(t), iri: toIri(t, 'type_prospections'), libelle: '', actif: true, ordre: 0 }
  }
  return {
    id: t.id ?? extractId(t['@id']),
    iri: toIri(t, 'type_prospections'),
    libelle: t.libelle ?? '',
    actif: t.actif ?? true,
    ordre: t.ordre ?? 0
  }
}

/** Si User n'expose pas le groupe prospection:read, on ne reçoit qu'un IRI. */
export const normalizeUser = (u) => {
  if (u && typeof u === 'object') {
    return {
      id: u.id ?? extractId(u['@id']),
      iri: toIri(u, 'users'),
      firstname: u.name ?? '',
      lastname: u.lastname ?? '',
      label: [u.lastname, u.name].filter(Boolean).join(' ')
    }
  }
  return { id: extractId(u), iri: toIri(u, 'users'), firstname: '', lastname: '', label: '' }
}

// Nom de l'entreprise : utile sur l'accueil et le tableau de bord
// (groupe prospection:read à ajouter sur Entreprise::id et ::name).
export const normalizeEntreprise = (e) => {
  if (!e) return null
  if (typeof e === 'object') {
    return { id: e.id ?? extractId(e['@id']), iri: toIri(e, 'entreprises'), name: e.name ?? '' }
  }
  return { id: extractId(e), iri: toIri(e, 'entreprises'), name: '' }
}

// L'API Qualifop sérialise en snake_case (name converter) :
// date_echeance, created_at... On repasse en camelCase ici, une fois,
// pour que les composants restent indépendants de ce choix.
// ── Relances ───────────────────────────────────────────────────────

export const RELANCE_A_FAIRE = 'a_faire'
export const RELANCE_FAITE = 'faite'
export const RELANCE_ANNULEE = 'annulee'

// Délai proposé par défaut pour la relance suivante
export const DELAI_RELANCE_JOURS = 7

export const normalizeRelance = (r) => {
  if (!r || typeof r !== 'object') return null
  return {
    id: r.id ?? extractId(r['@id']),
    iri: toIri(r, 'entreprise_prospect_relances'),
    // IRI de l'opportunité : le libellé est retrouvé côté front
    prospectIri: r.prospect ? toIri(r.prospect, 'entreprise_prospects') : null,
    intitule: r.intitule ?? '',
    datePrevue: r.date_prevue ?? r.datePrevue ?? null,
    statut: r.statut ?? RELANCE_A_FAIRE,
    responsable: r.responsable ? normalizeUser(r.responsable) : null,
    realiseePar: (r.realisee_par ?? r.realiseePar) ? normalizeUser(r.realisee_par ?? r.realiseePar) : null,
    dateRealisation: r.date_realisation ?? r.dateRealisation ?? null,
    commentaire: r.commentaire ?? null,
    createdAt: r.created_at ?? r.createdAt ?? null
  }
}

export const isRelanceEnRetard = (relance) =>
  relance?.statut === RELANCE_A_FAIRE && !!relance.datePrevue && joursAvant(relance.datePrevue) < 0

export const normalizeProspect = (p) => ({
  ...p,
  dateEcheance: p.date_echeance ?? p.dateEcheance ?? null,
  dateRelance: p.date_relance ?? p.dateRelance ?? null,
  dateIdentification: p.date_identification ?? p.dateIdentification ?? null,
  dateCloture: p.date_cloture ?? p.dateCloture ?? null,
  prochaineRelance: normalizeRelance(p.prochaine_relance ?? p.prochaineRelance),
  createdAt: p.created_at ?? p.createdAt ?? null,
  updatedAt: p.updated_at ?? p.updatedAt ?? null,
  iri: toIri(p, 'entreprise_prospects'),
  entreprise: normalizeEntreprise(p.entreprise),
  type: normalizeType(p.type),
  responsables: (p.responsables ?? []).map(normalizeUser)
})

// ── Utilisateurs ───────────────────────────────────────────────────

export const userLabel = (user) => {
  if (!user || typeof user !== 'object') return ''
  return [ user.lastname, user.name].filter(Boolean).join(' ')
}

export const userInitials = (user) => {
  if (!user || typeof user !== 'object') return '?'
  const f = (user.name || '').charAt(0)
  const l = (user.lastname || '').charAt(0)
  return (l + f).toUpperCase() || '?'
}
